import React from 'react';
import { createRoot } from 'react-dom/client';
import { AcquisitionForm } from './AcquisitionForm.jsx';
import { LoadingSkeleton } from './ui/LoadingSkeleton.jsx';
import { parseScriptParams } from './utils/parseScriptParams.js';
import { parseDataAttributes, inferDisplayMode } from './utils/parseDataAttributes.js';
import { clearConfigCache, loadConfig } from './utils/loadConfig.js';
import { loadLocalConfig } from './utils/loadLocalConfig.js';
import { mergeConfig } from './utils/mergeConfig.js';
import { collectDevCss, getDisplayMode } from './displayModes.js';
import { acquisitionFormStylesheetUrl, linkStylesheet, linkStylesheetToHead } from './utils/acquisitionFormStylesheet.js';
import devBaseCss from './styles/base.css?inline';
import devItiCss from 'intl-tel-input/dist/css/intlTelInput.css?inline';
import devItiOverridesCss from './styles/iti-overrides.css?inline';
import './styles/base.css';
import 'intl-tel-input/dist/css/intlTelInput.css';
import './styles/iti-overrides.css';

const CONFIG_SOURCES = ['Api', 'Local', 'Auto'];

// Linked instead of inlined so the embedding site can allow our origin in
// style-src rather than 'unsafe-inline'. The dev server has no built
// widget.css, so the harness keeps the inline path.
async function applyStyles(shadowRoot) {
  if (import.meta.env.DEV) {
    const css = `${devBaseCss}\n${devItiCss}\n${devItiOverridesCss}\n${collectDevCss()}`;
    const styleEl = document.createElement('style');
    styleEl.textContent = css;
    shadowRoot.appendChild(styleEl);

    if (!document.head.querySelector('style[data-tm-acquisition-form-css]')) {
      const headStyleEl = document.createElement('style');
      headStyleEl.setAttribute('data-tm-acquisition-form-css', '');
      headStyleEl.textContent = css;
      document.head.appendChild(headStyleEl);
    }
    return;
  }

  const href = acquisitionFormStylesheetUrl();
  if (href) await Promise.all([linkStylesheet(shadowRoot, href), linkStylesheetToHead(href)]);
}

function prefetchStyles() {
  if (import.meta.env.DEV) return;

  const href = acquisitionFormStylesheetUrl();
  if (href) linkStylesheetToHead(href);
}

const IS_DEVLOCAL_MODE = import.meta.env.MODE === 'devlocal';

function normalizeConfigSource(value, fallback = 'Auto') {
  return CONFIG_SOURCES.includes(value) ? value : fallback;
}

// data-tm-preview wins over the script's tm-preview so a single page can host
// both a live acquisition form and a preview one.
function resolvePreview(value, scriptPreview) {
  if (value == null) return scriptPreview;
  return value === 'true' || value === '1';
}

/**
 * Registry of all mounted instances.
 * Key: the original acquisition form host HTMLElement
 * Value: { shadowHost, root, unmount, getData }
 *
 * A WeakMap is not iterable, so destroyAll() walks a parallel Set that
 * mountInstance/unmount keep in sync.
 */
const instances = new WeakMap();
const mountedElements = new Set();

function getAcquisitionFormElements() {
  return Array.from(document.querySelectorAll('[data-tm-acquisition-form="donation"]'));
}

function shouldSkipMount(element, attrs, scriptPreview, effectiveSource, scriptOrganizationId) {
  const hasPreviewConfig = scriptPreview && attrs.configJson;

  if (!attrs.acquisitionFormId && !hasPreviewConfig) {
    console.warn(
      '[TrackmobAcquisitionForm] Skipping element - missing data-tm-acquisition-form-id attribute.',
      element
    );
    return true;
  }

  if (!scriptPreview && effectiveSource === 'Api' && !scriptOrganizationId) {
    console.warn(
      '[TrackmobAcquisitionForm] Skipping element - missing organizationId for API config source.',
      element
    );
    return true;
  }

  if (instances.has(element) || element.dataset.tmMounted === 'true') {
    console.warn('[TrackmobAcquisitionForm] Element already mounted, skipping.', element);
    return true;
  }

  // The base bundle does not ship the picker: rendering a plain form inside a
  // picker host would silently drop the cart, so say what is missing instead.
  const isBuiltInDisplay = attrs.display === 'Inline' || attrs.display === 'ButtonModal';
  if (attrs.display && !isBuiltInDisplay && !getDisplayMode(attrs.display)) {
    console.warn(
      `[TrackmobAcquisitionForm] Display mode "${attrs.display}" is not in this bundle — load widget-projects.js.`,
      element
    );
    return true;
  }

  return false;
}

async function resolveBaseConfig(attrs, scriptPreview, effectiveSource, scriptOrganizationId) {
  if (scriptPreview && attrs.configJson) {
    try {
      const parsed = JSON.parse(attrs.configJson);
      return parsed;
    } catch (error) {
      console.warn('[TrackmobAcquisitionForm] Invalid data-tm-config-json, skipping.', error);
      return null;
    }
  }

  if (effectiveSource === 'Local') {
    if (!IS_DEVLOCAL_MODE) {
      console.warn('[TrackmobAcquisitionForm] tm-config-source="Local" is available only in devlocal mode');
      return null;
    }

    return loadLocalConfig(attrs.acquisitionFormId);
  }

  if (effectiveSource === 'Api') {
    const apiConfig = await loadConfig(scriptOrganizationId, attrs.acquisitionFormId);
    return apiConfig;
  }

  if (scriptOrganizationId) {
    const apiConfig = await loadConfig(scriptOrganizationId, attrs.acquisitionFormId);
    if (apiConfig) {
      return apiConfig;
    }
  }

  if (IS_DEVLOCAL_MODE) {
    return loadLocalConfig(attrs.acquisitionFormId);
  }

  return null;
}

// A mode that occupies the host takes its place (Inline, and the picker);
// ButtonModal instead hides the trigger and mounts next to it.
function createShadowHost(element, replacesHost, acquisitionFormHostId) {
  const shadowHost = document.createElement('div');
  shadowHost.dataset.trackmobHost = acquisitionFormHostId;

  if (replacesHost) {
    element.innerHTML = '';
    element.appendChild(shadowHost);
    return shadowHost;
  }

  element.style.display = 'none';
  element.parentNode.insertBefore(shadowHost, element.nextSibling);
  return shadowHost;
}

function createMount(element, replacesHost, acquisitionFormHostId) {
  const shadowHost = createShadowHost(element, replacesHost, acquisitionFormHostId);
  const shadowRoot = shadowHost.attachShadow({ mode: 'open' });
  const mountPoint = document.createElement('div');
  shadowRoot.appendChild(mountPoint);

  return { shadowHost, shadowRoot, root: createRoot(mountPoint) };
}

function showLoadingSkeleton(element, acquisitionFormHostId) {
  const hostContent = Array.from(element.childNodes);
  const mount = createMount(element, true, acquisitionFormHostId);
  mount.root.render(<LoadingSkeleton />);

  return {
    ...mount,
    cancel() {
      mount.root.unmount();
      mount.shadowHost.remove();
      element.replaceChildren(...hostContent);
    }
  };
}

/**
 * Mounts an acquisition form instance on a single acquisition form host element.
 *
 * @param {HTMLElement} element
 * @param {string|null} scriptCampaignId
 * @param {string|null} scriptOrganizationId
 * @param {boolean} scriptPreview
 */
async function mountInstance(element, scriptCampaignId, scriptOrganizationId, scriptPreview, scriptConfigSource) {
  const attrs = parseDataAttributes(element);
  const scriptSource = normalizeConfigSource(scriptConfigSource, normalizeConfigSource(import.meta.env.VITE_ACQUISITION_FORM_CONFIG_SOURCE, 'Auto'));
  const effectiveSource = normalizeConfigSource(attrs.configSource, scriptSource);
  const isPreview = resolvePreview(attrs.preview, scriptPreview);

  if (shouldSkipMount(element, attrs, isPreview, effectiveSource, scriptOrganizationId)) {
    return;
  }

  const display = attrs.display || inferDisplayMode(element);
  const mode = getDisplayMode(display);
  const acquisitionFormHostId = attrs.acquisitionFormId || 'preview';
  const loading = display === 'Inline' ? showLoadingSkeleton(element, acquisitionFormHostId) : null;

  const baseConfig = await resolveBaseConfig(attrs, isPreview, effectiveSource, scriptOrganizationId);

  if (!baseConfig) {
    loading?.cancel();
    return;
  }

  const config = mergeConfig(baseConfig, scriptCampaignId, attrs);
  const Root = mode?.Root || AcquisitionForm;

  // A mode that reads a host payload cannot render without it — an empty picker
  // block is an authoring error, not something to paper over with a plain form.
  const hostPayload = mode?.parseHostPayload ? mode.parseHostPayload(element) : null;
  if (mode?.parseHostPayload && !hostPayload) {
    console.warn(
      `[TrackmobAcquisitionForm] Display mode "${display}" has no usable payload on this element, skipping.`,
      element
    );
    return;
  }

  const { shadowHost, shadowRoot, root } = loading
    ?? createMount(element, display === 'Inline' || Boolean(mode?.replacesHost), acquisitionFormHostId);

  await applyStyles(shadowRoot);

  function unmount() {
    root.unmount();
    shadowHost.remove();
    if (display === 'ButtonModal') {
      element.style.display = '';
    }
    instances.delete(element);
    mountedElements.delete(element);
    element.removeAttribute('data-tm-mounted');
  }

  function getData() {
    // Exposed for external access — formData is managed internally in AcquisitionForm state.
    // This is a reference hook; full data access requires a ref pattern if needed.
    return { acquisitionFormId: acquisitionFormHostId, campaignId: config.CampaignId };
  }

  instances.set(element, { shadowHost, root, unmount, getData });
  mountedElements.add(element);
  element.dataset.tmMounted = 'true';

  root.render(
    <Root
      config={config}
      display={display}
      hostPayload={hostPayload}
      triggerElement={element}
      isPreview={isPreview}
    />
  );
}

/**
 * Scans the DOM for all acquisition form host elements and mounts them.
 *
 * @param {string|null} scriptCampaignId
 * @param {string|null} scriptOrganizationId
 * @param {boolean} scriptPreview
 */
async function mountAll(scriptCampaignId, scriptOrganizationId, scriptPreview, scriptConfigSource) {
  const elements = getAcquisitionFormElements();

  if (elements.length === 0) {
    console.warn('[TrackmobAcquisitionForm] No [data-tm-acquisition-form="donation"] elements found on page.');
    return;
  }

  const mountPromises = Array.from(elements).map(el => mountInstance(el, scriptCampaignId, scriptOrganizationId, scriptPreview, scriptConfigSource));
  await Promise.all(mountPromises);

  console.log(`[TrackmobAcquisitionForm] Mounted ${elements.length} instance(s).`);
}

/**
 * Destroys all mounted instances and cleans up the DOM.
 */
function destroyAll() {
  for (const element of mountedElements) {
    const instance = instances.get(element);
    if (instance) instance.unmount();
  }
  mountedElements.clear();
  clearConfigCache();
}

/**
 * Re-scans the DOM for new acquisition form host elements.
 * Useful for SPAs where elements are added after initial load.
 */
async function refresh() {
  const { campaignId, organizationId, preview, configSource } = parseScriptParams();
  const elements = getAcquisitionFormElements();
  const newElements = elements.filter(el => !instances.has(el));

  if (newElements.length === 0) return;

  await Promise.all(newElements.map(el => mountInstance(el, campaignId, organizationId, preview, configSource)));
  console.log(`[TrackmobAcquisitionForm] Refreshed — mounted ${newElements.length} new instance(s).`);
}

/**
 * Entry point for every bundle. Called by src/entry/*.jsx after that bundle
 * registered the display modes it ships, so registration always precedes the
 * first mount.
 */
export function start() {
  const {
    campaignId: scriptCampaignId,
    organizationId: scriptOrganizationId,
    preview: scriptPreview,
    configSource: scriptConfigSource
  } = parseScriptParams();

  prefetchStyles();

  function boot() {
    mountAll(scriptCampaignId, scriptOrganizationId, scriptPreview, scriptConfigSource).then(() => {
      // Expose public API on window
      window.TrackmobAcquisitionForm = {
        instances,
        refresh,
        destroyAll,
        getInstance: (element) => instances.get(element) || null
      };
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    // DOM already ready (e.g. script loaded after DOMContentLoaded)
    boot();
  }
}
